/**
 * The CRM's sales-demo roster.
 *
 * A sample overlay for demonstrations, in the SAME shape the server returns
 * (`CrmContact`), so the page has one contact type and nothing adapts one
 * shape into another on the way to the screen. It is appended only under
 * `withDemo`, after the real records, and never on a tenant with demo data off.
 *
 * Trimmed from a 28-record roster that carried fabricated news headlines,
 * invented "recurring counterparties" keyed to these ids, and a deal-link
 * table that real deals fell through to. None of that survives: what is here
 * is a plain roster of eight sample parties, and everything the demo screen
 * shows about them is on these records.
 */
import type { CrmCommunication, CrmContact, CrmEnrichment } from '@/services/crm'

const EMPTY_ENRICHMENT: CrmEnrichment = {
  linkedIn: null,
  website: null,
  previousCompanies: [],
  yearsExperience: null,
  dealVolume: null,
  specialties: [],
  education: null,
  licenses: [],
  newsArticles: 0,
  news: [],
  lookedUpAt: null,
  sources: [],
  extra: [],
  pinnedFields: [],
}

type DemoSeed = {
  id: string
  firstName: string
  lastName: string
  email: string | null
  phone?: string | null
  title?: string | null
  company: string
  companyType: CrmContact['companyType']
  isEntity?: boolean
  isInternal?: boolean
  location?: string | null
  tags?: string[]
  lastContact?: string | null
  linkedDeals?: string[]
  dealHistory?: CrmContact['dealHistory']
  communications?: CrmCommunication[]
  enrichment?: Partial<CrmEnrichment>
  metrics?: CrmContact['metrics']
  notes?: string | null
}

function demo(seed: DemoSeed): CrmContact {
  const name = [seed.firstName, seed.lastName].filter(Boolean).join(' ').trim()
  return {
    id: seed.id,
    firstName: seed.firstName,
    lastName: seed.lastName,
    // Labeled where it is read (T-69, 2 Nov 2026: "where did that entry come from?"
    // about a sample CBRE contact's last contact). A sample row never passes for a
    // real one, in the table or on the profile.
    displayName: `${name || seed.email || seed.company} (sample)`,
    kind: seed.isEntity ? 'organisation' : 'person',
    email: seed.email,
    emails: seed.email ? [seed.email] : [],
    phone: seed.phone ?? null,
    phones: { primary: seed.phone ?? null, direct: null, mobile: null, office: null },
    address: null,
    title: seed.title ?? null,
    company: seed.company,
    companyId: null,
    companyType: seed.companyType,
    roleEvidence: null,
    isInternal: seed.isInternal ?? false,
    isEntity: seed.isEntity ?? false,
    editedFields: [],
    editedBy: null,
    capacities: [],
    linkedDeals: seed.linkedDeals ?? [],
    dealHistory: seed.dealHistory ?? [],
    score: null,
    lastContact: seed.lastContact ?? null,
    source: 'Sample data',
    status: 'Active',
    tags: ['Sample data', ...(seed.tags ?? [])],
    location: seed.location ?? null,
    communications: seed.communications ?? [],
    enrichment: { ...EMPTY_ENRICHMENT, ...(seed.enrichment ?? {}) },
    notes: seed.notes ?? null,
    metrics: seed.metrics ?? {},
    needsReview: false,
  }
}

export const DEMO_CRM_CONTACTS: CrmContact[] = [
  demo({
    id: 'CRM-001',
    firstName: 'Mike',
    lastName: 'Harris',
    email: 'mharris@cbre.com',
    phone: '(602) 555-0147',
    title: 'Vice President',
    company: 'CBRE',
    companyType: 'Broker',
    location: 'Phoenix, AZ',
    tags: ['Multifamily', 'Phoenix MSA'],
    lastContact: '2026-03-17T14:22:00Z',
    linkedDeals: ['ALG-1001', 'ALG-1008'],
    dealHistory: [
      { dealId: 'ALG-1001', property: 'Harbor Point Residences', amount: 62_500_000, stage: 'Triage', date: '2026-03-13', outcome: null, role: 'Broker' },
      { dealId: 'ALG-1008', property: 'Sunrise Senior Living', amount: 29_500_000, stage: 'Term Sheet Signed', date: '2026-01-20', outcome: null, role: 'Broker' },
    ],
    communications: [
      { id: 'c-001-1', type: 'Email', subject: 'Financing Request - Desert Ridge Apartments', preview: 'Attached is the package for a 320-unit garden-style complex in North Phoenix.', timestamp: '2026-03-17T14:22:00Z', direction: 'Inbound', user: null },
      { id: 'c-001-2', type: 'Call', subject: 'Deal discussion - Desert Ridge', preview: 'Discussed pricing, sponsor track record and timeline.', timestamp: '2026-03-18T16:30:00Z', direction: 'Outbound', user: null },
    ],
    enrichment: { linkedIn: 'linkedin.com/in/mikeharris-cbre', yearsExperience: 14, specialties: ['Multifamily', 'Build-to-Rent'], lookedUpAt: '2026-03-01T09:00:00Z', sources: [{ name: 'Firm website', ok: true, detail: null }] },
    metrics: { hitRate: 0.38, totalSubmissions: 13, fundedDeals: 5, isPreferred: true, stalkingHorseCount: 0, isRepeatOffender: false, commonSponsors: ['Greenfield RE Partners'] },
    notes: 'Strong relationship, always brings quality deals. Prefers quick indicative pricing before a formal application.',
  }),
  demo({
    id: 'CRM-002',
    firstName: 'Jennifer',
    lastName: 'Walsh',
    email: 'jennifer.walsh@jll.com',
    phone: '(214) 555-0283',
    title: 'Director, Debt Advisory',
    company: 'JLL',
    companyType: 'Broker',
    location: 'Dallas, TX',
    tags: ['Office', 'Industrial', 'Texas Markets'],
    lastContact: '2026-03-16T11:45:00Z',
    linkedDeals: ['ALG-1004'],
    dealHistory: [
      { dealId: 'ALG-1004', property: 'Legacy Park Office Campus', amount: 55_000_000, stage: 'Term Sheet Issued', date: '2026-02-15', outcome: null, role: 'Broker' },
    ],
    communications: [
      { id: 'c-002-1', type: 'Email', subject: 'Logistics Center Financing RFP', preview: 'Submitting on behalf of a Class A logistics facility in the DFW metroplex.', timestamp: '2026-03-16T11:45:00Z', direction: 'Inbound', user: null },
    ],
    metrics: { hitRate: 0.5, totalSubmissions: 8, fundedDeals: 4, isPreferred: false },
  }),
  demo({
    id: 'CRM-003',
    firstName: 'Amanda',
    lastName: 'Torres',
    email: 'atorres@nmrk.com',
    phone: '(305) 555-0331',
    title: 'Managing Director',
    company: 'Newmark',
    companyType: 'Broker',
    location: 'Miami, FL',
    tags: ['Mixed-Use', 'Florida'],
    lastContact: '2026-03-18T08:15:00Z',
    linkedDeals: ['ALG-1001'],
    dealHistory: [
      { dealId: 'ALG-1001', property: 'Harbor Point Residences', amount: 62_500_000, stage: 'Triage', date: '2026-03-13', outcome: null, role: 'Broker' },
    ],
    metrics: { hitRate: null, totalSubmissions: 3, fundedDeals: 0 },
  }),
  demo({
    id: 'CRM-006',
    firstName: 'James',
    lastName: 'Whitfield',
    email: 'jwhitfield@whitfielddev.com',
    phone: '(214) 555-0418',
    title: 'Chief Executive Officer',
    company: 'Whitfield Development',
    companyType: 'Borrower/Sponsor',
    location: 'Dallas, TX',
    tags: ['Office', 'Industrial', 'Repeat Borrower'],
    lastContact: '2026-03-18T16:00:00Z',
    linkedDeals: ['ALG-1004', 'ALG-1011'],
    dealHistory: [
      { dealId: 'ALG-1004', property: 'Legacy Park Office Campus', amount: 55_000_000, stage: 'Term Sheet Issued', date: '2026-02-15', outcome: null, role: 'Sponsor' },
      { dealId: 'ALG-1011', property: 'Whitfield Industrial Park', amount: 63_000_000, stage: 'Screening', date: '2026-03-01', outcome: null, role: 'Sponsor' },
    ],
    communications: [
      { id: 'c-006-1', type: 'Email', subject: 'Application Documents - Legacy Park', preview: 'Attached is the completed application package for Legacy Park Office.', timestamp: '2026-03-18T16:00:00Z', direction: 'Inbound', user: null },
    ],
    notes: 'Tier 1 sponsor. 22-year track record, zero defaults.',
  }),
  demo({
    id: 'CRM-007',
    firstName: 'Harbor Point Owner LLC',
    lastName: '',
    email: null,
    company: 'Harbor Point Owner LLC',
    companyType: 'Borrower/Sponsor',
    isEntity: true,
    location: 'Jersey City, NJ',
    tags: ['Multifamily'],
    lastContact: '2026-03-13T10:00:00Z',
    linkedDeals: ['ALG-1001'],
    dealHistory: [
      { dealId: 'ALG-1001', property: 'Harbor Point Residences', amount: 62_500_000, stage: 'Triage', date: '2026-03-13', outcome: null, role: 'Borrowing entity' },
    ],
  }),
  demo({
    id: 'CRM-010',
    firstName: 'Priya',
    lastName: 'Natarajan',
    email: 'pnatarajan@northstarcap.com',
    phone: '(212) 555-0640',
    title: 'Principal',
    company: 'Northstar Capital Partners',
    companyType: 'Investor',
    location: 'New York, NY',
    tags: ['Mezzanine', 'Preferred Equity'],
    lastContact: '2026-03-11T13:20:00Z',
    linkedDeals: ['ALG-1009'],
    dealHistory: [
      { dealId: 'ALG-1009', property: 'Gateway Industrial Portfolio', amount: 120_000_000, stage: 'IC / Committee Approved', date: '2026-01-05', outcome: null, role: 'Investor' },
    ],
  }),
  demo({
    id: 'CRM-013',
    firstName: 'Daniel',
    lastName: 'Osei',
    email: 'dosei@hallsworthlaw.com',
    phone: '(312) 555-0912',
    title: 'Partner, Real Estate Finance',
    company: 'Hallsworth & Reed LLP',
    companyType: 'Law Firm',
    location: 'Chicago, IL',
    tags: ["Lender's counsel"],
    lastContact: '2026-03-09T17:05:00Z',
    linkedDeals: ['ALG-1004'],
    dealHistory: [
      { dealId: 'ALG-1004', property: 'Legacy Park Office Campus', amount: 55_000_000, stage: 'Term Sheet Issued', date: '2026-02-15', outcome: null, role: 'Counsel' },
    ],
  }),
  demo({
    id: 'CRM-020',
    firstName: 'Sarah',
    lastName: 'Chen',
    email: 'schen@helios-fintech.com',
    phone: '(646) 555-0102',
    title: 'Managing Director, Originations',
    company: 'Helios',
    companyType: 'Internal',
    isInternal: true,
    location: 'New York, NY',
    tags: ['Originations'],
    lastContact: '2026-03-18T09:00:00Z',
    linkedDeals: ['ALG-1001', 'ALG-1004', 'ALG-1009'],
    dealHistory: [
      { dealId: 'ALG-1001', property: 'Harbor Point Residences', amount: 62_500_000, stage: 'Triage', date: '2026-03-13', outcome: null, role: 'Originator' },
      { dealId: 'ALG-1004', property: 'Legacy Park Office Campus', amount: 55_000_000, stage: 'Term Sheet Issued', date: '2026-02-15', outcome: null, role: 'Originator' },
      { dealId: 'ALG-1009', property: 'Gateway Industrial Portfolio', amount: 120_000_000, stage: 'IC / Committee Approved', date: '2026-01-05', outcome: null, role: 'Underwriter' },
    ],
  }),
]
